import type { EventData } from "@/services/sanity/types";

export interface EventDateInfo {
    isMultiDay: boolean;
    isAllDay: boolean;
    badgeMonth: string;
    badgeDay: string;
    badgeWeekday: string;
    formattedDate: string;
    formattedTime: string;
    modalTimeDetails?: string;
}

/**
 * Formats event dates and times consistently across all cards, list rows, and modal dialogs.
 * Correctly distinguishes between single-day, multi-day, and all-day events.
 */
export function formatEventDateInfo(
    event: EventData,
    t: (key: string, options?: Record<string, unknown>) => string
): EventDateInfo {
    const startDate = new Date(event.date);
    const hasValidStart = !isNaN(startDate.getTime());

    if (!hasValidStart) {
        return {
            isMultiDay: false,
            isAllDay: false,
            badgeMonth: "",
            badgeDay: "",
            badgeWeekday: "",
            formattedDate: event.date,
            formattedTime: "",
        };
    }

    const endDate = event.endDate ? new Date(event.endDate) : null;
    const hasValidEnd = endDate ? !isNaN(endDate.getTime()) : false;

    // Check if event spans multiple calendar days in NZ time
    let isMultiDay = false;
    if (hasValidEnd && endDate) {
        const startDayStr = startDate.toLocaleDateString("en-NZ", {
            timeZone: "Pacific/Auckland",
            year: "numeric",
            month: "numeric",
            day: "numeric",
        });
        const endDayStr = endDate.toLocaleDateString("en-NZ", {
            timeZone: "Pacific/Auckland",
            year: "numeric",
            month: "numeric",
            day: "numeric",
        });
        isMultiDay = startDayStr !== endDayStr;
    }

    const isAllDay = Boolean(event.isAllDay);

    // 1. Stylized Date Badge (Month, Day, Weekday)
    const startMonth = startDate
        .toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", month: "short" })
        .toUpperCase();
    const startDay = startDate.toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", day: "numeric" });
    const startWeekday = startDate
        .toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "short" })
        .toUpperCase();

    let badgeMonth = startMonth;
    let badgeDay = startDay;
    let badgeWeekday = startWeekday;

    if (isMultiDay && endDate) {
        const endMonth = endDate
            .toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", month: "short" })
            .toUpperCase();
        const endDay = endDate.toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", day: "numeric" });
        const endWeekday = endDate
            .toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "short" })
            .toUpperCase();

        badgeMonth = startMonth === endMonth ? startMonth : `${startMonth}–${endMonth}`;
        badgeDay = `${startDay}–${endDay}`;
        badgeWeekday = `${startWeekday}–${endWeekday}`;
    }

    // 2. Full Formatted Date String
    let formattedDate = "";
    if (!isMultiDay || !endDate) {
        formattedDate = startDate.toLocaleDateString("en-NZ", {
            timeZone: "Pacific/Auckland",
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } else {
        const startPart = startDate.toLocaleDateString("en-NZ", {
            timeZone: "Pacific/Auckland",
            weekday: "short",
            day: "numeric",
            month: "short",
        });
        const endPart = endDate.toLocaleDateString("en-NZ", {
            timeZone: "Pacific/Auckland",
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        formattedDate = `${startPart} – ${endPart}`;
    }

    // 3. Formatted Time String & Modal Details
    let formattedTime = "";
    let modalTimeDetails: string | undefined;

    if (isAllDay) {
        formattedTime = isMultiDay
            ? t("events.multiDayGathering") || t("events.allDay") || "All Day"
            : t("events.allDay") || "All Day";
    } else {
        const startTime = startDate.toLocaleTimeString("en-NZ", {
            timeZone: "Pacific/Auckland",
            hour: "numeric",
            minute: "2-digit",
        });

        if (isMultiDay && endDate) {
            const startWk = startDate.toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "short" });
            const endWk = endDate.toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "short" });
            const endTime = endDate.toLocaleTimeString("en-NZ", {
                timeZone: "Pacific/Auckland",
                hour: "numeric",
                minute: "2-digit",
            });
            formattedTime = `${startWk} ${startTime} – ${endWk} ${endTime}`;

            const startLongDay = startDate.toLocaleDateString("en-NZ", {
                timeZone: "Pacific/Auckland",
                weekday: "long",
                day: "numeric",
                month: "short",
            });
            const endLongDay = endDate.toLocaleDateString("en-NZ", {
                timeZone: "Pacific/Auckland",
                weekday: "long",
                day: "numeric",
                month: "short",
            });
            modalTimeDetails = `${startLongDay} at ${startTime} – ${endLongDay} at ${endTime}`;
        } else if (hasValidEnd && endDate) {
            const endTime = endDate.toLocaleTimeString("en-NZ", {
                timeZone: "Pacific/Auckland",
                hour: "numeric",
                minute: "2-digit",
            });
            formattedTime = `${startTime} – ${endTime}`;
        } else {
            formattedTime = startTime;
        }
    }

    return {
        isMultiDay,
        isAllDay,
        badgeMonth,
        badgeDay,
        badgeWeekday,
        formattedDate,
        formattedTime,
        modalTimeDetails,
    };
}
